import React, {useState, useEffect, useContext} from 'react';
import ConfigContext from '../contexts/configContext'
import {RTCVideo} from '@dmc/react-rtc-media-components';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import Popover from '@mui/material/Popover';
import VideocamIcon from '@mui/icons-material/Videocam';
import MicIcon from '@mui/icons-material/Mic';
import MicNoneTwoToneIcon from '@mui/icons-material/MicNoneTwoTone';
import MicNoneIcon from '@mui/icons-material/MicNone';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import SoundMeter from '../lib/soundmeter';
import WebRTCTest from './WebRTCTest';
import DefaultPoster from '../images/default-video-poster.png'
import { getDummyVideoTrack } from '../helpers/StreamHelpers';

export default function Devices({cameraAllowed, onBegin, onStream, onAudioDevices, onVideoDevices}) {
  const config = useContext(ConfigContext)
  const [iceServers, setIceServers] = useState(null)
  const [videoDefaultDisabled, setVideoDefaultDisabled] = useState(false)
  const [gotDevices, setGotDevices] = useState(false)
  const [audioInputs, setAudioInputs] = useState([])
  const [videoInputs, setVideoInputs] = useState([])
  const [selectedAudioInputId, setSelectedAudioInputId] = useState('')
  const [selectedVideoInputId, setSelectedVideoInputId] = useState('')
  const [selectedStream, setSelectedStream] = useState(null)
  const [hasDummyVideoTrack, setHasDummyVideoTrack] = useState(false)
  const [meterIntervalId, setMeterIntervalId] = useState(null)
  const [inputVolume, setInputVolume] = useState(0)
  const [testsAnchorElem, setTestsAnchorElem] = useState(null)
  const [hasMicPerm, setHasMicPerm] = useState(false)
  const [hasCamPerm, setHasCamPerm] = useState(false)

  useEffect(() => {
    if (config?.call?.iceServers) {
      setIceServers(config.call.iceServers)
    }
    if (config?.devices?.hasOwnProperty('videoDefaultDisabled')) setVideoDefaultDisabled(config.devices.videoDefaultDisabled)
    checkMicPerm()
  }, [])

  useEffect(() => {
    if (cameraAllowed) checkCamPerm()
  }, [cameraAllowed])

  useEffect(() => {
    enumerateDevices()
  }, [hasMicPerm, hasCamPerm, videoDefaultDisabled])

  const checkMicPerm =() => {
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(function(stream) {
        stream.getTracks().forEach(track => track.stop())
        setHasMicPerm(true)
      })
  }

  const checkCamPerm =() => {
    navigator.mediaDevices.getUserMedia({ video: true })
      .then(function(stream) {
        stream.getTracks().forEach(track => track.stop())
        setHasCamPerm(true)
      })
  }
  
  const enumerateDevices = () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.log("enumerateDevices() not supported.");
      return;
    }

    let aInputs = []
    let vInputs = []

    navigator.mediaDevices.enumerateDevices()
    .then((devices) => {
      devices.forEach(function(device) {
        if (device.kind === "audioinput") aInputs.push(device)
        if (device.kind === "videoinput") vInputs.push(device)
      });
      setAudioInputs(aInputs)
      setVideoInputs(vInputs)
      setGotDevices(true)
      // if (aInputs.length > 0) setSelectedAudioInputId(aInputs[0].deviceId)
      // if (videoEnabled ) {
      //   if (vInputs.length > 0 && !videoDefaultDisabled) {
      //     console.log('shouldnt be running :(', videoDefaultDisabled)
      //     setSelectedVideoInputId(vInputs[0].deviceId)
      //   }
      // }
    })
    .catch(function(err) {
      console.log(err.name + ": " + err.message);
    });

  }

  useEffect(() => {
    if (!audioInputs || audioInputs.length < 1) return
    onAudioDevices(audioInputs)
    setSelectedAudioInputId(audioInputs[0].deviceId)
  }, [audioInputs])

  useEffect(() => {
    if (!videoInputs || videoInputs.length < 1) return
    onVideoDevices(videoInputs)
    if (videoDefaultDisabled) {
      setSelectedVideoInputId('')
    } else {
      setSelectedVideoInputId(videoInputs[0].deviceId)
    }
  }, [videoInputs, videoDefaultDisabled])

  useEffect(() => {
    if (!hasMicPerm) return
    if (cameraAllowed && !hasCamPerm) return
    if (!selectedAudioInputId) return
    const getStream = async () => {
      let finalstream = null
      let stream = null
      let constraints = { 
        audio: {deviceId: selectedAudioInputId},
        video: false
      }
      if (cameraAllowed && selectedVideoInputId !== '') {
        constraints['video'] = 
          {
            deviceId: { exact: selectedVideoInputId }
          }
        if (config?.call?.videoConstraints) {
          constraints['video'] = { ...constraints['video'], ...config.call.videoConstraints }
        }
      }
      
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints)
        if (stream.getVideoTracks().length < 1) {
          finalstream = new MediaStream([stream.getAudioTracks()[0], getDummyVideoTrack()])
          setHasDummyVideoTrack(true)
        } else {
          finalstream = new MediaStream([stream.getAudioTracks()[0], stream.getVideoTracks()[0]])
          setHasDummyVideoTrack(false)
        }
        setSelectedStream(finalstream)
      } catch (err) {
        console.log(err)
      }

    }
    getStream();

  }, [selectedAudioInputId, selectedVideoInputId, hasCamPerm, hasMicPerm, cameraAllowed])

  useEffect(() => {
    let meterInterval;

    if (selectedStream) {
      onStream(selectedStream, selectedAudioInputId, selectedVideoInputId, hasDummyVideoTrack)

      if (meterIntervalId) clearInterval(meterIntervalId)

      try {
        window.AudioContext = window.AudioContext || window.webkitAudioContext;
        window.audioContext = new AudioContext();
      } catch (e) {
        alert('Web Audio API not supported.');
      }

      const soundMeter = window.soundMeter = new SoundMeter(window.audioContext);
      soundMeter.connectToSource(selectedStream, function(e) {
        if (e) {
          alert(e);
          return;
        }
        meterInterval = setInterval(() => {
          // meterRef.current.value = soundMeter.instant.toFixed(2)
          setInputVolume(soundMeter.instant.toFixed(2))
        }, 200);
        setMeterIntervalId(meterInterval)
      });
    }

  }, [selectedStream, hasDummyVideoTrack, onStream])

  useEffect(() => {
    return () => {
      if (meterIntervalId) {
        clearInterval(meterIntervalId)
      }
    }
  }, [meterIntervalId])

  const handleAudioDeviceChange = (event) => {
    setSelectedAudioInputId(event.target.value)
  }

  const handleVideoDeviceChange = (event) => {
    setSelectedVideoInputId(event.target.value)
  }

  const renderMicIcon = () => {
    let icon = null
    if (inputVolume > 0 && inputVolume < 0.2) {
      icon = <MicNoneTwoToneIcon color='primary' sx={{m:2}}/> 
    } else if (inputVolume > 0.1) {
      icon = <MicIcon color='primary' sx={{m:2}}/> 
    } else {
      icon = <MicNoneIcon color='primary' sx={{m:2}}/> 
    }
    return icon
  }

  const renderWarnings = () => {
    let warnings = []
    if (!hasMicPerm) warnings.push("Unable to access Microphone - Please grant access")
    if (cameraAllowed && !hasCamPerm) warnings.push("Unable to access Camera - Please grant access")
    return (
      <div className='live-call-devices-warnings'>
        <Stack sx={{ width: '100%' }} spacing={1}>
          {warnings.map((warning,i) => 
            <Alert key={i} severity="error">{warning}</Alert>
          )}
        </Stack>
      </div>
    )
  }

  const testsOpen = Boolean(testsAnchorElem);
  const testsId = testsOpen ? 'webrtc-tests-popover' : undefined;

  return (
    <div className='live-call-devices'>
        {gotDevices ? 
          <>
            <div className='live-call-devices-local-video'>
              <RTCVideo
                srcObject={selectedStream}
                style={{
                  display: cameraAllowed ? 'block' : 'none',
                  width: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  transform: !selectedStream ? "rotateY(0deg)" : "rotateY(180deg)",
                  MozTransform: !selectedStream ? "rotateY(0deg)" : "rotateY(180deg)",
                }}
                poster={config?.devices?.posterUrl ?
                  config.devices.posterUrl
                  :  
                  `${DefaultPoster}`}
                autoPlay
              />
              <div 
                className='live-call-devices-test-icon'
              >
                <IconButton 
                  onClick={(event) => {
                    setTestsAnchorElem(event.currentTarget)
                  }}
                >
                  <NetworkCheckIcon
                      color='primary'
                  />
                </IconButton>
                <Popover
                  id={testsId}
                  open={testsOpen}
                  anchorEl={testsAnchorElem}
                  onClose={() => {
                    setTestsAnchorElem(null)
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                >
                  <WebRTCTest iceServers={iceServers}/>
                </Popover>
              </div>
              </div>

            {!hasCamPerm || (cameraAllowed && !hasCamPerm)
              ? renderWarnings()
              :
              <div className='live-call-devices-local-controls'>
                <div style={{flexGrow: 1}}>
                  {cameraAllowed ?
                    <div className='flex-select'>
                      <VideocamIcon color='primary'sx={{m:2}}/>
                      <div style={{flexGrow: 1}}>
                        <FormControl margin='normal' fullWidth>
                            <InputLabel id='video-label'>Video</InputLabel>
                            <Select
                              sx={{ width: '100%' }}
                              label="Video"
                              labelId="video-label"
                              id="live-call-video-inputs"
                              value={selectedVideoInputId}
                              onChange={handleVideoDeviceChange}
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Disabled</em>
                              </MenuItem>
                              {videoInputs.map((device, i) => 
                                <MenuItem key={i} value={device.deviceId}>{device.label}</MenuItem>
                              )}
                            </Select>
                        </FormControl>
                      </div>
                    </div>
                  : null}
                  <div className='flex-select'>
                    {/* <MicIcon color='primary' sx={{m:2}}/> */}
                    {renderMicIcon()}
                    {/* <div style={{position: 'relative'}}> */}
                    <div style={{flexGrow: 1}}>
                      <FormControl margin='normal' fullWidth>
                        <InputLabel id='audio-label'>Audio</InputLabel>
                        <Select
                          sx={{ width: '100%' }}
                          labelId="audio-label"
                          label="Audio"
                          id="live-call-audio-inputs"
                          value={selectedAudioInputId}
                          onChange={handleAudioDeviceChange}
                          displayEmpty
                        >
                          {audioInputs.map((device, i) => 
                            <MenuItem key={i} value={device.deviceId}>{device.label}</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </div>
                  </div>

                </div>
                <div className='flex-child'>
                  <Button 
                    sx={{height: 54, marginTop: '7px', maxWidth: 'fit-content'}}
                    variant="contained"
                    size="large"
                    disabled={!selectedStream ? true : false}
                    onClick={onBegin}
                  >
                    {config?.devices?.beginButtonTxt
                        ? config.devices.beginButtonTxt
                        : "Begin"
                    }
                  </Button>
                </div>
              </div>
            }
          </>
        : null}
        
    </div>
  )
}
